/**
 * =============================================================================
 * IDS CRM Web - Distributors List Page
 * =============================================================================
 *
 * PURPOSE:
 * Lists all distributors with hierarchy view, search, filtering, and counts.
 * Shows master distributors with their sub-distributor and farm counts.
 * Links to distributor detail page for full chain view.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatMoney } from '../utils/money';
import { getCountryName } from '../utils/countries';
import { formatDateKey } from '../utils/dates';
import { getCapabilities, isFieldEngineer } from '../utils/permissions';
import { useListMemory, useRememberList } from '../hooks/useListMemory';
import './Distributors.css';

// Farm-site customers get REAL computed health (nightly customerHealth run);
// distributor rows never did — their fictional chip was removed in R5b (G19).
const getHealthClass = (score) => {
  if (score === 'green') return 'health-badge green';
  if (score === 'amber') return 'health-badge amber';
  if (score === 'red') return 'health-badge red';
  return 'health-badge';
};

function Distributors() {
  const { authFetch, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Coming back from a distributor or a farm (a "←" link or the browser's
  // Back) shows the list as it was left; a fresh visit starts from the address.
  const memory = useListMemory('/distributors');

  const [distributors, setDistributors] = useState([]);
  const [regions, setRegions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => memory.initial('search', searchParams.get('search') || ''));
  // Set when a reply shows the page asked for is past the last page there is.
  const [pastLastPage, setPastLastPage] = useState(null);
  const [expandedChains, setExpandedChains] = useState({});
  // The chains that were open when the list was left, by distributor. Only
  // the ids are remembered: each chain is loaded again on coming back.
  const chainsLeftOpen = useRef(memory.initial('chains', []));
  // A chain being loaded again still counts as open until its load has
  // settled, so leaving the list before then does not forget it.
  const [chainsReopening, setChainsReopening] = useState(() => (
    Array.isArray(chainsLeftOpen.current) ? chainsLeftOpen.current : []
  ));

  const page = parseInt(searchParams.get('page') || '1', 10);
  const level = searchParams.get('level') || '';
  const region = searchParams.get('region') || '';

  // The level, region and page are in the address, which is remembered with
  // it. The search box filters as it is typed, before Search puts it in the
  // address, so its text is remembered here, with the chains left open.
  useRememberList('/distributors', {
    search,
    chains: [...new Set([...Object.keys(expandedChains), ...chainsReopening])]
  });

  const fetchDistributors = useCallback(async () => {
    setLoading(true);
    let steppingBack = false;
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '25'
      });

      if (search) params.append('search', search);
      if (level) params.append('level', level);
      if (region) params.append('region', region);

      const response = await authFetch(`/api/v1/distributors?${params}`);
      const data = await response.json();

      if (response.ok) {
        // A page past the end (the list got shorter while a distributor was
        // open) steps back to the last page there is: see the effect below.
        // The list stays loading until that page arrives, so it never reads
        // as empty.
        const lastPage = Math.max(1, data.pagination?.totalPages || 0);
        if (page > lastPage) {
          steppingBack = true;
          setPastLastPage({ page, lastPage });
          return;
        }
        setDistributors(data.data);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch distributors:', error);
    } finally {
      if (!steppingBack) setLoading(false);
    }
  }, [authFetch, page, search, level, region]);

  const fetchRegions = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/distributors/meta/regions');
      const data = await response.json();
      if (response.ok) {
        setRegions(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch regions:', error);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchDistributors();
  }, [fetchDistributors]);

  // The page is in the address, so the step back is made there, in place of
  // the page that no longer exists (the browser's Back does not return to
  // it). Only if the address is still on the page the reply was for.
  useEffect(() => {
    if (!pastLastPage) return;
    setPastLastPage(null);
    if (page !== pastLastPage.page) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(pastLastPage.lastPage));
    setSearchParams(newParams, { replace: true });
  }, [pastLastPage, page, searchParams, setSearchParams]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (search) {
      newParams.set('search', search);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage.toString());
    setSearchParams(newParams);
  };

  const loadChain = useCallback(async (distributorId) => {
    try {
      const response = await authFetch(`/api/v1/distributors/${distributorId}/chain`);
      const data = await response.json();
      if (response.ok) {
        setExpandedChains(prev => ({
          ...prev,
          [distributorId]: data
        }));
      }
    } catch (error) {
      console.error('Failed to fetch distributor chain:', error);
    }
  }, [authFetch]);

  // Coming back: the chains left open are opened again, once. The list is
  // emptied so a later run of this effect cannot reopen a chain since closed.
  useEffect(() => {
    const ids = Array.isArray(chainsLeftOpen.current) ? chainsLeftOpen.current : [];
    chainsLeftOpen.current = [];
    ids.forEach(async (distributorId) => {
      await loadChain(distributorId);
      // Open now, or it could not be loaded: either way no longer on its way.
      setChainsReopening(prev => prev.filter(id => id !== distributorId));
    });
  }, [loadChain]);

  const toggleChainExpand = async (distributorId) => {
    if (expandedChains[distributorId]) {
      setExpandedChains(prev => {
        const next = { ...prev };
        delete next[distributorId];
        return next;
      });
    } else {
      await loadChain(distributorId);
    }
  };

  const getLevelLabel = (distributorLevel) => {
    const labels = {
      master: 'Master',
      sub: 'Sub'
    };
    return labels[distributorLevel] || distributorLevel || '-';
  };

  // partner_since and contract_renewal_date are DATE columns: calendar days.
  const formatDate = (date) => formatDateKey(date);

  return (
    <div className="distributors-page">
      <div className="page-header">
        <div>
          <h1>Distributors</h1>
          <p>{pagination.total} total partners</p>
        </div>
        {getCapabilities(user).manage_distributors && (
          <Link to="/customers/new?type=distributor" className="btn-primary">
            + New Distributor
          </Link>
        )}
      </div>

      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">Search</button>
        </form>

        <div className="filters">
          <select
            value={level}
            onChange={(e) => handleFilterChange('level', e.target.value)}
            className="filter-select"
          >
            <option value="">All Levels</option>
            <option value="master">Master Distributors</option>
            <option value="sub">Sub-Distributors</option>
          </select>

          <select
            value={region}
            onChange={(e) => handleFilterChange('region', e.target.value)}
            className="filter-select"
          >
            <option value="">All Regions</option>
            {regions.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading distributors...</div>
      ) : distributors.length === 0 ? (
        <div className="empty-state">
          <p>No distributors found</p>
          {search && <p>Try adjusting your search or filters</p>}
        </div>
      ) : (
        <>
          <div className="distributors-list">
            {distributors.map((distributor) => (
              <div key={distributor.customer_id} className="distributor-card">
                <div className="distributor-header">
                  <div className="distributor-info">
                    <div className="distributor-level">
                      <span className={`level-badge ${distributor.distributor_level}`}>
                        {getLevelLabel(distributor.distributor_level)}
                      </span>
                      <span className="distributor-number">{distributor.customer_number}</span>
                    </div>
                    <Link to={`/customers/${distributor.customer_id}`} className="distributor-name">
                      {distributor.business_name}
                    </Link>
                    {distributor.parent_distributor_name && (
                      <div className="parent-info">
                        Under: {distributor.parent_distributor_name}
                      </div>
                    )}
                  </div>

                </div>

                <div className="distributor-meta">
                  <div className="meta-item">
                    <span className="meta-label">Regions</span>
                    <span className="meta-value">
                      {distributor.regions_covered?.join(', ') || '-'}
                    </span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Country</span>
                    <span className="meta-value">{getCountryName(distributor.country_code, '-')}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Partner Since</span>
                    <span className="meta-value">{formatDate(distributor.partner_since)}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Renewal</span>
                    <span className="meta-value">{formatDate(distributor.contract_renewal_date)}</span>
                  </div>
                </div>

                <div className="distributor-stats">
                  <div className="stat-item">
                    <span className="stat-number">{distributor.sub_distributor_count || 0}</span>
                    <span className="stat-label">Sub-Distributors</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{distributor.direct_farm_count || 0}</span>
                    <span className="stat-label">Direct Farms</span>
                  </div>
                  <div className="stat-item highlight">
                    <span className="stat-number">{distributor.total_farm_count || 0}</span>
                    <span className="stat-label">Total Farms</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{distributor.active_asset_count || 0}</span>
                    <span className="stat-label">Active Assets</span>
                  </div>
                </div>

                {/* Commercial rollup — the server strips these fields for
                    field engineers, so the block hides rather than show £0 */}
                {!isFieldEngineer(user?.role) && (
                  <div className="distributor-performance">
                    <div className="stat-item">
                      <span className="stat-number">{distributor.open_opportunity_count || 0}</span>
                      <span className="stat-label">Open Deals</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{formatMoney(distributor.open_pipeline_gbp_minor, 'GBP', { maximumFractionDigits: 0 })}</span>
                      <span className="stat-label">Pipeline (GBP)</span>
                    </div>
                    <div className="stat-item highlight">
                      <span className="stat-number">{formatMoney(distributor.won_revenue_12m_gbp_minor, 'GBP', { maximumFractionDigits: 0 })}</span>
                      <span className="stat-label">Won 12m ({distributor.won_count_12m || 0})</span>
                    </div>
                  </div>
                )}

                <div className="distributor-actions">
                  <Link to={`/customers/${distributor.customer_id}`} className="action-link">
                    View Details
                  </Link>
                  <Link to={`/hierarchy/distributor/${distributor.customer_id}`} className="action-link">
                    Hierarchy
                  </Link>
                  {(distributor.sub_distributor_count > 0 || distributor.total_farm_count > 0) && (
                    <button
                      onClick={() => toggleChainExpand(distributor.customer_id)}
                      className="action-button"
                    >
                      {expandedChains[distributor.customer_id] ? 'Hide Chain' : 'Show Chain'}
                    </button>
                  )}
                </div>

                {expandedChains[distributor.customer_id] && (
                  <div className="chain-view">
                    <ChainView chain={expandedChains[distributor.customer_id]} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= pagination.totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChainView({ chain }) {
  return (
    <div className="chain-content">
      <div className="chain-summary">
        <span>{chain.summary.subDistributorCount} sub-distributors</span>
        <span>{chain.summary.directFarmCount} direct farms</span>
        <span>{chain.summary.indirectFarmCount} via sub-distributors</span>
        {chain.summary.depotCount > 0 && <span>{chain.summary.depotCount} depots</span>}
      </div>

      {(chain.depots || []).length > 0 && (
        <div className="chain-section">
          <h4>Depots &amp; the sites they service</h4>
          {chain.depots.map(depot => (
            <div key={depot.depot_id} className="chain-depot">
              <div className="chain-depot-header">
                <span className="chain-depot-badge">D</span>
                <span className="item-name">{depot.depot_name}</span>
                {depot.is_head_office && <span className="chain-depot-ho">Head office</span>}
                <span className="item-location">{[depot.town_city, depot.postcode].filter(Boolean).join(', ')}</span>
                <span className="item-count">
                  {depot.serviced_sites.length} site{depot.serviced_sites.length === 1 ? '' : 's'}
                </span>
              </div>
              {depot.serviced_sites.length > 0 && (
                <div className="chain-list chain-depot-sites">
                  {depot.serviced_sites.map(site => (
                    <Link
                      key={site.site_id}
                      to={`/customers/${site.customer_id}`}
                      className="chain-item farm"
                    >
                      <span className="item-number">{site.customer_number}</span>
                      <span className="item-name">{site.business_name}</span>
                      <span className="item-location">{site.site_name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

          {(chain.unassignedSites || []).length > 0 && (
            <div className="chain-depot chain-depot-unassigned">
              <div className="chain-depot-header">
                <span className="chain-depot-badge unassigned">?</span>
                <span className="item-name">No depot assigned</span>
                <span className="item-count">{chain.unassignedSites.length} site{chain.unassignedSites.length === 1 ? '' : 's'}</span>
              </div>
              <div className="chain-list chain-depot-sites">
                {chain.unassignedSites.map(site => (
                  <Link
                    key={site.site_id}
                    to={`/customers/${site.customer_id}?tab=sites`}
                    className="chain-item farm"
                  >
                    <span className="item-number">{site.customer_number}</span>
                    <span className="item-name">{site.business_name}</span>
                    <span className="item-location">{site.site_name}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {chain.subDistributors.length > 0 && (
        <div className="chain-section">
          <h4>Sub-Distributors</h4>
          <div className="chain-list">
            {chain.subDistributors.map(sub => (
              <Link
                key={sub.customer_id}
                to={`/customers/${sub.customer_id}`}
                className="chain-item sub-distributor"
              >
                <span className="item-number">{sub.customer_number}</span>
                <span className="item-name">{sub.business_name}</span>
                <span className="item-count">{sub.farm_count} farms</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {chain.directFarms.length > 0 && (
        <div className="chain-section">
          <h4>Direct Farms</h4>
          <div className="chain-list">
            {chain.directFarms.map(farm => (
              <Link
                key={farm.customer_id}
                to={`/customers/${farm.customer_id}`}
                className="chain-item farm"
              >
                <span className={getHealthClass(farm.health_score)} />
                <span className="item-number">{farm.customer_number}</span>
                <span className="item-name">{farm.business_name}</span>
                <span className="item-location">{farm.town_city}, {farm.country_code}</span>
                <span className="item-count">{farm.asset_count} assets</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {chain.subDistributorFarms.length > 0 && (
        <div className="chain-section">
          <h4>Farms via Sub-Distributors</h4>
          <div className="chain-list">
            {chain.subDistributorFarms.map(farm => (
              <Link
                key={farm.customer_id}
                to={`/customers/${farm.customer_id}`}
                className="chain-item farm indirect"
              >
                <span className={getHealthClass(farm.health_score)} />
                <span className="item-number">{farm.customer_number}</span>
                <span className="item-name">{farm.business_name}</span>
                <span className="item-via">via {farm.sub_distributor_name}</span>
                <span className="item-count">{farm.asset_count} assets</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Distributors;
